import { MAGMA_POOL_ZONE, type MatchSettings } from '../types';
import { ARENA_MIN_RADIUS, MAX_PLAYERS, MIN_PLAYERS_TO_START } from '../physics/constants';
import { clamp } from '../physics/vec2';

/** `settings.startRadius` is tuned for this many players. */
export const REFERENCE_PLAYER_COUNT = 8;

type ShrinkSettings = Pick<MatchSettings, 'graceMs' | 'shrinkIntervalMs' | 'shrinkPct'>;

/**
 * Starting radius for a round, scaled so the area per player stays roughly constant
 * (2-player rounds get a smaller arena than 12-player ones).
 */
export function arenaStartRadius(startRadius: number, playerCount: number): number {
  const players = clamp(playerCount, MIN_PLAYERS_TO_START, MAX_PLAYERS);
  return Math.max(ARENA_MIN_RADIUS, startRadius * Math.sqrt(players / REFERENCE_PLAYER_COUNT));
}

/**
 * Safe radius `elapsedMs` into a fight. Holds `startRadius` for the grace period, then loses
 * `shrinkPct` of the starting radius per step (the first step lands when grace ends), never
 * going below ARENA_MIN_RADIUS.
 */
export function arenaRadiusAt(
  elapsedMs: number,
  startRadius: number,
  settings: ShrinkSettings,
): number {
  if (elapsedMs < settings.graceMs || settings.shrinkPct <= 0) return startRadius;
  const steps = Math.floor((elapsedMs - settings.graceMs) / settings.shrinkIntervalMs) + 1;
  const radius = startRadius * (1 - steps * settings.shrinkPct);
  return Math.max(Math.min(ARENA_MIN_RADIUS, startRadius), radius);
}

/**
 * The last safe radius above ARENA_MIN_RADIUS in the shrink sequence (`arenaRadiusAt`'s
 * `startRadius × (1 − k × shrinkPct)`), i.e. the ring before the final one; `startRadius` itself
 * when the first step already reaches the minimum. Null when the arena never shrinks
 * (`shrinkPct` 0 or a start radius at the minimum).
 */
export function penultimateRadius(startRadius: number, shrinkPct: number): number | null {
  if (shrinkPct <= 0 || startRadius <= ARENA_MIN_RADIUS) return null;
  const radiusAt = (k: number) => startRadius * (1 - k * shrinkPct);
  let k = Math.max(0, Math.ceil((1 - ARENA_MIN_RADIUS / startRadius) / shrinkPct) - 1);
  // Settle float rounding with the exact expression arenaRadiusAt uses.
  while (k > 0 && radiusAt(k) <= ARENA_MIN_RADIUS) k--;
  while (radiusAt(k + 1) > ARENA_MIN_RADIUS) k++;
  return radiusAt(k);
}

/** Time (fight-elapsed ms) of the next shrink step after `elapsedMs`, or null once at minimum. */
export function nextShrinkAt(
  elapsedMs: number,
  startRadius: number,
  settings: ShrinkSettings,
): number | null {
  if (settings.shrinkPct <= 0) return null;
  if (arenaRadiusAt(elapsedMs, startRadius, settings) <= ARENA_MIN_RADIUS) return null;
  if (elapsedMs < settings.graceMs) return settings.graceMs;
  const steps = Math.floor((elapsedMs - settings.graceMs) / settings.shrinkIntervalMs) + 1;
  return settings.graceMs + steps * settings.shrinkIntervalMs;
}

/** Whether a point is on stone (true) or lava (false) for the current arena. */
export function isOnArena(
  x: number,
  y: number,
  radius: number,
  shape: MatchSettings['arenaShape'],
): boolean {
  return shape === 'square'
    ? Math.abs(x) <= radius && Math.abs(y) <= radius
    : x * x + y * y <= radius * radius;
}

/**
 * Evenly spaced spawn points around the arena edge, `inset` units inside it, rotated by
 * `angleOffset` radians.
 */
export function spawnPoints(
  count: number,
  radius: number,
  inset: number,
  angleOffset = 0,
): { x: number; y: number }[] {
  const angles = Array.from({ length: count }, (_, i) => (i / count) * Math.PI * 2 + angleOffset);
  return spawnPointsAt(angles, radius, inset);
}

/** Spawn points at the given angles (radians, see `spawnAngles`), `inset` inside the edge. */
export function spawnPointsAt(
  angles: readonly number[],
  radius: number,
  inset: number,
): { x: number; y: number }[] {
  const r = Math.max(0, radius - inset);
  return angles.map((angle) => ({ x: Math.cos(angle) * r, y: Math.sin(angle) * r }));
}

/** A Magma Pool as lava sees it: a disc. */
export interface LavaPool {
  x: number;
  y: number;
  r: number;
}

/** A synced zone, as far as Magma Pool ownership goes. */
export interface PoolZone extends LavaPool {
  kind: string;
  ownerId?: string;
  team?: number;
}

/**
 * The Magma Pools that are lava for `player`: every `magmaPool` zone cast by someone else who is
 * not its teammate (Teams mode, team ≠ 0). The caster and allies walk on their own pools as on
 * stone. Server lava, movement, client prediction and bots all filter with this.
 */
export function lavaPoolsFor(
  zones: Iterable<PoolZone>,
  player: { sessionId?: string; team?: number },
  teams: boolean,
): LavaPool[] {
  const pools: LavaPool[] = [];
  for (const z of zones) {
    if (z.kind !== MAGMA_POOL_ZONE) continue;
    if (z.ownerId !== undefined && z.ownerId === player.sessionId) continue;
    const team = player.team ?? 0;
    if (teams && team !== 0 && z.team === team) continue;
    pools.push({ x: z.x, y: z.y, r: z.r });
  }
  return pools;
}

/** Whether (x, y) is inside any of `pools`. */
export function inLavaPool(x: number, y: number, pools: readonly LavaPool[] | undefined): boolean {
  if (!pools) return false;
  for (const p of pools) if (Math.hypot(x - p.x, y - p.y) < p.r) return true;
  return false;
}

/**
 * Whether a body at `p` stands on lava: off the arena (`edge` absent = no fight, no arena lava) or
 * inside one of the Magma Pools that burn it (`lavaPoolsFor`). The one rule for lava damage,
 * Emberward and the stone-only surface mods (Fleetfoot).
 */
export function isOnLavaFor(
  p: { x: number; y: number },
  edge: { radius: number; shape: MatchSettings['arenaShape'] } | undefined,
  pools?: readonly LavaPool[],
): boolean {
  if (edge && !isOnArena(p.x, p.y, edge.radius, edge.shape)) return true;
  return inLavaPool(p.x, p.y, pools);
}
